import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import {
    azureResourceSelectMethodOptions, commonFileTypeStoreGetMetadataActivityReadSettings
} from 'app/registries/consts/metadata-consts';
import { mergeCredentialOption } from '../utls/util';
import { Constants } from 'app/shared/shared-constants';
import { storageUrlForm } from 'app/shared/constants/links.const';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { compact } from 'lodash';

const storageUrl = format(storageUrlForm, 'dfs');

function azureBlobFSAccountSetting(fetchAccountKey: boolean): ControlMetadata {
    const manualCase: ControlMetadata[] = [{
        type: 'textbox',
        bind: 'url',
        label: stringResources.ModelName_Url,
        required: true,
        placeholder: storageUrl,
        validators: [
            { type: 'required', errorMessage: '' },
            {
                type: 'pattern',
                pattern: `^https://[^\/\.]+\\.dfs\\.(${Constants.storageValidEndpointSuffixes})/?$|^[*]{10}$`,
                errorMessage: format(stringResources.Validator_ErrorMessage_Pattern, storageUrl),
            },
            {
                type: 'pattern',
                pattern: `^https://[a-z0-9-]{3,}\\.dfs\\.(${Constants.storageValidEndpointSuffixes})/?$|^[*]{10}$`,
                errorMessage: stringResources.Validation_StorageAccountName,
            },
        ],
        isLabelVertical: true
    }];

    if (fetchAccountKey) {
        manualCase.push({
            type: 'dynamic-akv-switcher',
            bind: 'accountKey',
            label: stringResources.ModelName_StorageAccountKey,
            child: {
                type: 'password',
                label: stringResources.ModelName_StorageAccountKey,
                required: true,
                isLabelVertical: true
            },
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureBlobFS_DisplayText),
                stringResources.ModelName_StorageAccountKey)
        });
    }

    return ({
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.AzureStorage_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{url != null ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-storage-account-select',
                        bind: {
                            url: 'url',
                            accountKey: 'accountKey',
                            scope: '_accountResourceID'
                        },
                        blobfsOnly: true,
                        fetchAccountKey,
                        validators: fetchAccountKey ? {
                            accountName: [
                                { type: 'required', errorMessage: '' }
                            ],
                            accountKey: [
                                { type: 'required', errorMessage: '' }
                            ]
                        } : {
                            accountName: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: manualCase
            }
        ]
    });
}

export const azureDataLakeStoreGen2CosmosStructuredStreamMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureDataLakeStoreGen2CosmosStructuredStream.linkedServiceType,
        displayName: stringResources.AzureDataLakeStoreGen2CosmosStructuredStream_DisplayText,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-AzureDataLake'
        },
        notSupportCopySink: true,
        isFirstClass: true,
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.azureDataLakeStoreGen2CosmosStructuredStream.copySourceType],
        supportRootFolder: true,
        resourceTypes: ['Microsoft.Storage/storageAccounts'],
        category: ['Azure'],
        supportDatasetV2: true,
        linkedServiceNamePrefix: 'AzureDataLakeStorage',
        containerLabel: stringResources.FileSystem_DisplayText,
        containerLabelPlural: stringResources.FileSystems_DisplayText,
        supportGetMetadataActivity: true,
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AdlsGen2CosmosStructuredStream' as ResourceKind
        },
        supportedAuthKindsInTrident: compact([ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ]),
        supportOPDG: GlobalVariables.enableOPDG,
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        controls: [
            {
                type: 'select-switch',
                bind: 'authenticationType',
                label: stringResources.Authentication_Method_Label,
                options: mergeCredentialOption([
                    {
                        label: stringResources.AuthenticationType_ManagedServiceIdentity,
                        value: AuthenticationType.ManagedServiceIdentity
                    },
                    {
                        label: stringResources.AuthenticationType_ServicePrincipal,
                        value: AuthenticationType.ServicePrincipal
                    },
                    {
                        label: stringResources.AuthenticationType_AccountKey,
                        value: AuthenticationType.AccountKey
                    }
                ]),
                isLabelVertical: true,
                defaultValue: AuthenticationType.AccountKey,
                cases: [
                    {
                        caseValue: AuthenticationType.AccountKey,
                        children: [
                            azureBlobFSAccountSetting(true)
                        ]
                    },
                    {
                        caseValue: AuthenticationType.ManagedServiceIdentity,
                        children: [
                            azureBlobFSAccountSetting(false),
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureDataLakeStoreGen2CosmosStructuredStream_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-data-lake-storage?tabs=data-factory#managed-identity'
                            }
                        ]
                    },
                    {
                        caseValue: AuthenticationType.ServicePrincipal,
                        children: [
                            azureBlobFSAccountSetting(false),
                            {
                                type: 'tenant-textbox',
                                bind: 'tenant',
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                isLabelVertical: true,
                                label: stringResources.ModelName_Tenant,
                                required: true
                            },
                            {
                                type: 'textbox',
                                bind: 'servicePrincipalId',
                                label: stringResources.ModelName_ServicePrincipalID,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                isLabelVertical: true
                            },
                            {
                                type: 'dynamic-akv-switcher',
                                bind: 'servicePrincipalKey',
                                label: stringResources.ModelName_ServicePrincipalKey,
                                child: {
                                    type: 'password',
                                    label: stringResources.ModelName_ServicePrincipalKey,
                                    required: true,
                                    isLabelVertical: true
                                },
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureDataLakeStoreGen2CosmosStructuredStream_DisplayText),
                                    stringResources.ModelName_ServicePrincipalKey)
                            }
                        ]
                    },
                    {
                        caseValue: AuthenticationType.Credential,
                        children: [
                            azureBlobFSAccountSetting(false),
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        ]
                    }
                ]
            }
        ]
    };
    return metadata;
};
